import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as ProcoreIframeHelpers from '@procore/procore-iframe-helpers';
import { ClientService } from 'src/app/services/client.service';
import { ContextService } from 'src/app/services/context.service';
import { DropboxService } from 'src/app/services/dropbox.service';
import { ProcoreService } from 'src/app/services/procore.service';

@Component({
	selector: 'app-landing',
	templateUrl: './landing.component.html',
	styleUrls: ['./landing.component.css']
})
export class LandingComponent implements OnInit {
	authError = false;
	authMessage;

	constructor(
		private fb: UntypedFormBuilder,
		private dropboxService: DropboxService,
		private contextService: ContextService,
		private clientService: ClientService,
		private procoreService: ProcoreService,
		private router: Router,
		private route: ActivatedRoute
	) { }

	/**
	 * This is the initial component that gets loaded when the page loads.  This component
	 * is responsible for setting the context of the user and the side-panel.
	 * 
	 * The following occurs within this component
	 * 
	 * 1. Load the query parameters to determine if the user is
	 * 		- A. initially loading the side-panel
	 * 		- B. just authenticated against Dropbox Sign and being redirected back to the app
	 * 		- C. just accepted the Procore auth popup
	 * 	    - D. just launched a new signature request in a new tab
	 * 2. 
	 */

	// 1. https://cool-cat-cd2045.netlify.app					INITIAL SIDE-PANEL LOAD
	// 2. https://cool-cat-cd2045.netlify.app?code=123			POST PROCORE AUTH
	// 3. https://cool-cat-cd2045.netlify.app?code=123&state=	...
	// 4. 

	ngOnInit() {
		this.loadQueryParams();
	}

	loadQueryParams() {
		this.contextService.procoreIsSandbox = document.referrer.indexOf('sandbox') > -1 || document.referrer.indexOf('procore-dbs-dev.shakeit.ai') > -1;

		this.route.queryParams
			.subscribe({
				next: (params) => {
					// Look for the code param and authenticate the user if not present.
					if (params['error'] && params['error_description']) {
						this.authMessage = 'Dropbox authentication failed with the following message: ' + params['error_description'] + '. Please  try again and contact support@shakeit.ai if the problem persists.';
					} else if (params['code'] && params['state']) {
						// If there is a code and state, the user just signed into Dropbox Sign and was redirected back to the app
						this.dropboxService.createDropboxUserAuth(params['code'], params['state']).subscribe((result) => {
							this.authMessage = result;
						});
					} else if (params['code']) {
						// If there is only a code, the user just accepted the Procore auth popup
						console.log('DS - PROCORE AUTHENTICATED: ' + params['code']);
						this.contextService.procoreAuthCode = params['code'];

						const context = ProcoreIframeHelpers.initialize();
						context.authentication.notifySuccess({ code: this.contextService.procoreAuthCode });

						this.loadSidepanelContext();
					} else if (params['embedded'] === '1') {
						// D - Handle the launching of a request in a new tab
						this.router.navigate(['/embedded'], { queryParams: { url: params['url'], companyId: params['companyId'], projectId: params['projectId'], context: params['context'], isSandbox: params['isSandbox'] } });
					} else {
						// A - Initial page load
						console.log('DS - ******************************');
						console.log('DS - DROPBOX SIGN FOR PROCORE');
						console.log('DS - ******************************');
						console.log('DS - INITIAL PAGE LOAD');
						this.procoreAuth();
					}
				}
			});
	}

	procoreAuth() {
		let url = '';
		let redirectUrl = '';
		if (this.contextService.procoreIsSandbox) {
			console.log('DS - AUTHENTICATE PROCORE: SANDBOX');
			redirectUrl = 'https://procore-dbs-dev.shakeit.ai';
			//redirectUrl = 'https://eclectic-starlight-444f6c.netlify.app';
			url = 'https://sandbox.procore.com/oauth/authorize?response_type=code&client_id=6ogNu3Q2o5ubr3iOox4cK54fRnta4q1qlpYCvZJP-GA&redirect_uri=' + redirectUrl;
		} else {
			console.log('DS - AUTHENTICATE PROCORE: PRODUCTION');
			redirectUrl = 'https://procore-dbs.shakeit.ai';
			//redirectUrl = 'https://eclectic-starlight-444f6c.netlify.app';
			url = 'https://login.procore.com/oauth/authorize?response_type=code&client_id=JOKHLVXaKHlvCb8D6vKuEwSDLSVg2dFtNeSvVOTDtz0&redirect_uri=' + redirectUrl;
		}

		const context = ProcoreIframeHelpers.initialize();
		const _this = this;

		context.authentication.authenticate({
			url: url,
			onSuccess: function (payload) {
				window.location.href = redirectUrl + '?code=' + payload.code;
			},
			onFailure: function (error) {
				console.log('DS - PROCORE AUTH FAILED: error');
				_this.authError = true;
			}
		});
	}

	// At this point we have the code only
	loadSidepanelContext() {
		window.addEventListener('message', (event) => {
			if (event.data.context) {
				this.contextService.procoreContextObject = event.data;
				console.log('DS - SET SIDE-PANEL CONTEXT: ');
				console.log(this.contextService.procoreContextObject);

				if (this.contextService.procoreContextObject.context.view === 'prime_contracts.change_orders.potential_change_orders.detail') { // TODO: remove this once prod manifest is fixed
					this.contextService.procoreContextObject.context.view = 'prime_contracts.change_orders.detail';
				}
				if (this.contextService.procoreContextObject.context.view === 'rfi.detail') {
					this.contextService.isRfi = true;
				}

				this.procoreService.getUserInfo(this.contextService.procoreContextObject.context.company_id, this.contextService.procoreAuthCode, this.contextService.procoreIsSandbox)
					.subscribe({
						next: (userInfo: any) => {
							this.contextService.procoreUserInfo = userInfo;
							console.log('DS - SET USER INFO:');
							console.log(this.contextService.procoreUserInfo);

							this.clientService.getClientDetailProcoreByCompanyIdAndProjectId(this.contextService.procoreContextObject.context.company_id, this.contextService.procoreContextObject.context.project_id)
								.subscribe({
									next: (clientDetailProcore: any) => {

										if (!clientDetailProcore) {
											console.log('DS - NO CLIENT DETAIL PROCORE, NAVIGATE TO WELCOME');
											this.router.navigate(['/welcome']);
										} else {
											console.log('DS - SET CLIENT DETAIL PROCORE:');
											console.log(clientDetailProcore);
											this.contextService.clientDetailProcore = clientDetailProcore;

											if (clientDetailProcore.dropboxSignAccountType == 'API') {
												console.log('DS - API: NAVIGATE TO WELCOME AUTHENTICATE');
												this.router.navigate(['/welcome/authenticate']);
											} else if (clientDetailProcore.dropboxSignAccountType == 'OAUTH') {
												console.log('DS - OAUTH: NAVIGATE TO WELCOME AUTHENTICATE');
												this.router.navigate(['/welcome/authenticate']);
											} else if (clientDetailProcore.dropboxSignAccountType == 'OAUTHG') {
												console.log('DS - OAUTHG: NAVIGATE TO WELCOME AUTHENTICATE');
												this.router.navigate(['/welcome/authenticate']);
											} else {
												console.log('DS - NAVIGATE TO WELCOME SELECT SUBSCRIPTION TYPE');
												this.router.navigate(['/welcome/type']);
											}
										}
									}
								});
						}
					});
			}
		});

		window.parent.postMessage({ type: 'initialize' }, '*');
	}
}
